import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  addAccount,
  deleteAccount,
  fetchAccounts,
  updateAccount,
} from "../../../actions/accountAction";
import { fetchRoles } from "../../../actions/roleAction";
import { fetchUserProfile } from "../../../actions/authAction/loginAction";
import { AlertConfirmModal } from "../../../components/Modals/AlertConfirmModal";
import AkasSelect from "../../../components/AkasSelect";
import { getActiveOutletId } from "../../../utils/helpers/outletContext";
import { hasAnyPermission, isFullAccessProfile } from "../../../utils/helpers/permissions";
import { sortHelper } from "../../../utils/helpers/sortHelper";
import AccountAvatar from "./AccountAvatar";
import FormAccount from "./FormAccount";
import "./style.css";

const createInitialState = () => ({
  id: "",
  name: "",
  username: "",
  email: "",
  role_id: "",
  role_name: "",
  phone: "",
  password: "",
  pos_stock_source: "outlet",
  image: "",
  image_path: "",
  outlet_ids: getActiveOutletId() ? [getActiveOutletId()] : [],
});

const isProtectedSystemRole = (roleName) =>
  ["owner", "super admin"].includes(String(roleName || "").trim().toLowerCase());

const AccountIcon = ({ name, size = 20 }) => {
  const paths = {
    plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" /><path d="m9 12 2 2 4-4" /></>,
    store: <><path d="M3 21h18" /><path d="M5 21V8l7-4 7 4v13" /><path d="M9 21v-6h6v6" /></>,
    search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></>,
    edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
    trash: <><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="m19 6-1 14H6L5 6" /><path d="M10 11v5M14 11v5" /></>,
    chevronLeft: <path d="m15 18-6-6 6-6" />,
    chevronRight: <path d="m9 18 6-6-6-6" />,
    sparkle: <><path d="m12 3-1.5 4.5L6 9l4.5 1.5L12 15l1.5-4.5L18 9l-4.5-1.5Z" /><path d="m19 16-.75 2.25L16 19l2.25.75L19 22l.75-2.25L22 19l-2.25-.75Z" /></>,
  };

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name] || paths.users}
    </svg>
  );
};

export const AccountPage = ({
  accounts,
  totalData,
  isLoading,
  error,
  roles,
  userProfile,
  fetchAccounts,
  fetchRoles,
  fetchUserProfile,
  addAccount,
  updateAccount,
  deleteAccount,
}) => {
  const [perPage, setPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formState, setFormState] = useState(createInitialState);
  const [pendingDelete, setPendingDelete] = useState(null);
  const { sortConfig, handleSort, getSortedData } = sortHelper.useSort();

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchAccounts(perPage, currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch, fetchAccounts, perPage]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const total = Number(totalData || 0);
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const rows = useMemo(() => getSortedData(accounts || []), [accounts, getSortedData]);
  const representedRoles = useMemo(
    () => new Set((accounts || []).map((account) => account?.role_id).filter(Boolean)).size,
    [accounts],
  );
  const outletAssignments = useMemo(
    () => (accounts || []).reduce((count, account) => count + (account?.outlets?.length || 0), 0),
    [accounts],
  );
  const pageNumbers = useMemo(() => {
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);
    return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index);
  }, [currentPage, totalPages]);

  const refreshOptions = { limit: perPage, page: currentPage, search: debouncedSearch };

  const openCreate = () => {
    setIsEdit(false);
    setFormState(createInitialState());
    setShowForm(true);
  };

  const openEdit = (account) => {
    setIsEdit(true);
    setFormState({
      ...createInitialState(),
      id: account?.id || "",
      name: account?.name || "",
      email: account?.email || "",
      role_id: account?.role_id || "",
      role_name: account?.role_name || "",
      username: account?.username || "",
      pos_stock_source: account?.pos_stock_source || "outlet",
      phone: account?.phone || "",
      image: account?.image || "",
      image_path: account?.image_path || account?.image || "",
      outlet_ids: Array.isArray(account?.outlet_ids) && account.outlet_ids.length
        ? account.outlet_ids.map(Number)
        : (account?.outlets || []).map((outlet) => Number(outlet.outlet_id || outlet.id)).filter(Boolean),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    if (isLoading) return;
    setShowForm(false);
    setIsEdit(false);
    setFormState(createInitialState());
  };

  const handleAdd = async () => {
    const success = await addAccount(formState, refreshOptions);
    if (success) {
      closeForm();
      if (typeof fetchUserProfile === "function") fetchUserProfile();
    }
    return success;
  };

  const handleUpdate = async () => {
    const success = await updateAccount(formState.id, formState, refreshOptions);
    if (success) {
      closeForm();
      if (typeof fetchUserProfile === "function") fetchUserProfile();
    }
    return success;
  };

  const handleDelete = async (id) => {
    const success = await deleteAccount(id, refreshOptions);
    if (success) {
      setPendingDelete(null);
      if (typeof fetchUserProfile === "function") fetchUserProfile();
    }
    return success;
  };

  const errorMessage = error?.message || error?.data?.message || (typeof error === "string" ? error : "");

  return (
    <div className="page-wrapper account-page">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row align-items-center g-3">
            <div className="col">
              <div className="page-pretitle">PENGGUNA & AKSES</div>
              <h2 className="page-title">Akun Tim</h2>
              <p className="text-secondary mb-0">Kelola kasir, staf, role, dan outlet yang dapat mereka akses.</p>
            </div>
            <div className="col-auto">
              <button type="button" className="btn btn-primary account-add-button" onClick={openCreate}>
                <AccountIcon name="plus" />
                <span>Tambah Akun</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <section className="account-summary-grid" aria-label="Ringkasan akun">
            <article className="account-summary-card">
              <span><AccountIcon name="users" /></span>
              <div><small>Total akun</small><strong>{total}</strong></div>
            </article>
            <article className="account-summary-card">
              <span><AccountIcon name="shield" /></span>
              <div><small>Role pada halaman ini</small><strong>{representedRoles}</strong></div>
            </article>
            <article className="account-summary-card">
              <span><AccountIcon name="store" /></span>
              <div><small>Akses outlet terlihat</small><strong>{outletAssignments}</strong></div>
            </article>
            <article className="account-summary-note">
              <AccountIcon name="sparkle" />
              <div>
                <strong>Akses tetap terkontrol</strong>
                <p>Role menentukan kewenangan, sedangkan penugasan outlet membatasi lokasi kerja setiap akun.</p>
              </div>
            </article>
          </section>

          {errorMessage ? <div className="alert alert-danger" role="alert">{errorMessage}</div> : null}

          <section className="card account-table-card">
            <div className="card-header account-table-header">
              <div>
                <span className="account-eyebrow">DIREKTORI TIM</span>
                <h3 className="card-title">Daftar akun</h3>
                <p className="text-secondary m-0">Identitas login dan cakupan akses operasional.</p>
              </div>
              <label className="account-search" htmlFor="account-search">
                <AccountIcon name="search" size={18} />
                <input
                  id="account-search"
                  type="search"
                  className="form-control"
                  value={search}
                  autoComplete="off"
                  placeholder="Cari nama, username, atau email..."
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setCurrentPage(1);
                  }}
                />
              </label>
            </div>

            <div className="table-responsive">
              <table className="table table-vcenter card-table account-table mb-0">
                <thead>
                  <tr>
                    <th className="w-1">No.</th>
                    <sortHelper.SortableHeader label="Nama" sortKey="name" sortConfig={sortConfig} onSort={handleSort} />
                    <th>Kontak</th>
                    <sortHelper.SortableHeader label="Role" sortKey="role_name" sortConfig={sortConfig} onSort={handleSort} />
                    <th>Outlet</th>
                    <th>Status</th>
                    <th className="text-end">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && !rows.length ? (
                    Array.from({ length: 5 }, (_, index) => (
                      <tr key={`account-loading-${index}`}>
                        <td>{index + 1}.</td>
                        <td><div className="account-loading-person"><span className="placeholder" /><div><i className="placeholder" /><i className="placeholder" /></div></div></td>
                        {Array.from({ length: 5 }, (__, cell) => <td key={cell}><div className="placeholder placeholder-lg w-75" /></td>)}
                      </tr>
                    ))
                  ) : rows.length ? (
                    rows.map((account, index) => {
                      const protectedAccount = isProtectedSystemRole(account?.role_name);
                      const canManage = isFullAccessProfile(userProfile) || !protectedAccount;
                      return (
                        <tr key={account?.id}>
                          <td>{(currentPage - 1) * perPage + index + 1}.</td>
                          <td>
                            <div className="account-person">
                              <AccountAvatar name={account?.name} image={account?.image_path || account?.image} size="md" />
                              <div>
                                <strong>{account?.name || "Tanpa nama"}</strong>
                                <small>@{account?.username || "-"}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="account-contact">
                              <strong>{account?.email || "-"}</strong>
                              <small>{account?.phone || "Nomor HP belum diisi"}</small>
                            </div>
                          </td>
                          <td><span className="account-role-badge">{account?.role_name || "Tanpa role"}</span></td>
                          <td>
                            <div className="account-outlet-list">
                              {(account?.outlets || []).slice(0, 2).map((outlet) => (
                                <span key={outlet.outlet_id || outlet.id}>{outlet.outlet_name || outlet.name}</span>
                              ))}
                              {(account?.outlets?.length || 0) > 2 ? <span>+{account.outlets.length - 2}</span> : null}
                              {!account?.outlets?.length ? <span className="is-empty">Belum diatur</span> : null}
                            </div>
                          </td>
                          <td><span className="account-status"><i /> Aktif</span></td>
                          <td className="text-end">
                            {canManage ? (
                              <div className="account-row-actions">
                                <button type="button" className="account-action is-edit" title="Edit akun" aria-label={`Edit akun ${account?.name || ""}`.trim()} onClick={() => openEdit(account)}>
                                  <AccountIcon name="edit" size={17} />
                                </button>
                                <button type="button" className="account-action is-delete" title="Hapus akun" aria-label={`Hapus akun ${account?.name || ""}`.trim()} data-bs-toggle="modal" data-bs-target="#modal-alert-account-delete" onClick={() => setPendingDelete(account)}>
                                  <AccountIcon name="trash" size={17} />
                                </button>
                              </div>
                            ) : <span className="badge bg-primary-lt">Akun pemilik</span>}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7">
                        <div className="account-empty">
                          <span><AccountIcon name="users" size={26} /></span>
                          <strong>{debouncedSearch ? "Akun tidak ditemukan" : "Belum ada akun tim"}</strong>
                          <p>{debouncedSearch ? "Coba gunakan kata kunci lain." : "Tambahkan akun agar staf dapat mulai bekerja di AKAS."}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <footer className="account-table-footer">
              <div className="account-per-page">
                <span>Tampilkan</span>
                <AkasSelect className="form-select form-select-sm" value={perPage} onChange={(event) => { setPerPage(Number(event.target.value)); setCurrentPage(1); }} isSearchable={false}>
                  {[25, 50, 100].map((value) => <option key={value} value={value}>{value}</option>)}
                </AkasSelect>
                <span>data</span>
              </div>
              <p>{rows.length} dari {total} akun</p>
              <nav aria-label="Navigasi halaman akun">
                <ul className="pagination m-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button type="button" className="page-link" aria-label="Halaman sebelumnya" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}><AccountIcon name="chevronLeft" size={17} /></button>
                  </li>
                  {pageNumbers.map((page) => (
                    <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                      <button type="button" className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button type="button" className="page-link" aria-label="Halaman berikutnya" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}><AccountIcon name="chevronRight" size={17} /></button>
                  </li>
                </ul>
              </nav>
            </footer>
          </section>
        </div>
      </div>

      <FormAccount
        canConfigureSellerStock={hasAnyPermission(userProfile, ["configure-seller-stocks"])}
        roles={roles}
        outlets={userProfile?.outlets || []}
        isShowModal={showForm}
        isEdit={isEdit}
        closeModal={closeForm}
        formState={formState}
        setFormState={setFormState}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
        isLoading={isLoading}
      />

      <AlertConfirmModal
        type="error"
        title="Hapus akun ini?"
        message={`${pendingDelete?.name || "Akun"} tidak akan dapat mengakses AKAS lagi.`}
        confirmText="Hapus Akun"
        cancelText="Batal"
        idModal="alert-account-delete"
        idData={pendingDelete?.id}
        waitForConfirm
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  accounts: state?.accounts?.datas?.data || [],
  totalData: state?.accounts?.datas?.total || 0,
  isLoading: state?.accounts?.loading,
  error: state?.accounts?.error,
  roles: state?.roles?.datas?.data || [],
  userProfile: state?.auth?.login?.profile,
});

export default connect(mapStateToProps, {
  fetchAccounts,
  addAccount,
  updateAccount,
  deleteAccount,
  fetchRoles,
  fetchUserProfile,
})(AccountPage);
